import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  Eye,
  Syringe,
  Stethoscope,
  CalendarClock,
  Info,
  FileText,
  FileCheck,
  CalendarOff,
  CalendarCheck,
  Phone,
  Building2,
  AlertTriangle,
} from "lucide-react";
import { AuthContext } from "../../../context/auth-context";
import { useHttpClient } from "../../../../shared/hooks/http-hook";
import "./ModalNotificacoesPermanentes.scss";

function ModalNotificacoesPermanentes({ open, onClose, onClickNotificacao }) {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const [notificacoes, setNotificacoes] = useState([]);
  const [filteredNotificacoes, setFilteredNotificacoes] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filtros
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [tipoFiltro, setTipoFiltro] = useState("todos");

  const needsDoctorInfo = ["Assistente", "Secretária(o)"].includes(auth.role);

  // Buscar notificações permanentes
  useEffect(() => {
    if (open) {
      fetchNotificacoesPermanentes();
    }
  }, [open]);

  // Modificar a função fetchNotificacoesPermanentes
  const fetchNotificacoesPermanentes = async () => {
    setLoading(true);
    try {
      // Construir query params
      const params = new URLSearchParams();

      if (dataInicio) {
        params.append("dataInicio", dataInicio);
      }

      if (dataFim) {
        params.append("dataFim", dataFim);
      }

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/notificacoespermanentes/${
          auth.userId
        }?${params.toString()}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        }
      );
      setNotificacoes(responseData.notificacoes);
      setFilteredNotificacoes(responseData.notificacoes);
    } catch (err) {
      console.error("Erro ao buscar notificações permanentes:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchNotificacoesPermanentes();
    }
  }, [dataInicio, dataFim]); // Busca novamente quando as datas mudam

  useEffect(() => {
    let filtered = [...notificacoes];

    // Filtro por tipo
    if (tipoFiltro !== "todos") {
      filtered = filtered.filter((n) => n.tipo === tipoFiltro);
    }

    setFilteredNotificacoes(filtered);
  }, [tipoFiltro, notificacoes]);

  const getNotificationIcon = (tipo) => {
    const iconStyle = { fontSize: "20px", marginRight: "12px", flexShrink: 0 };

    switch (tipo) {
      case "espera avaliação":
        return <FileText style={{ ...iconStyle, color: "rgb(245, 124, 0)" }} />;
      case "espera observação":
        return <Eye style={{ ...iconStyle, color: "#9c27b0" }} />;
      case "espera consulta":
        return <Stethoscope style={{ ...iconStyle, color: "#2e7d32" }} />;
      case "criou tratamento isolado":
        return <Syringe style={{ ...iconStyle, color: "rgb(93, 64, 55)" }} />;
      case "efetuou avaliação":
        return <FileCheck style={{ ...iconStyle, color: "#0288d1" }} />;
      case "reagendamento":
        return <CalendarClock style={{ ...iconStyle, color: "#d32f2f" }} />;
      case "agendamentosOrfaos":
        return <CalendarOff style={{ ...iconStyle, color: "#e74c3c" }} />;
      case "limpezaRealizada":
        return <CalendarCheck style={{ ...iconStyle, color: "#00897b" }} />;
      case "agendamentoProvisorioCriado":
        return <CalendarCheck style={{ ...iconStyle, color: "#00897b" }} />;
      case "lembreteAgendamentoProvisorio":
        return <Phone style={{ ...iconStyle, color: "#ff6f00" }} />;
      case "EMPRESA_PARCEIRA_EXPIRADA":
        return <Building2 style={{ ...iconStyle, color: "#f44336" }} />;
      case "EMPRESA_PARCEIRA_A_EXPIRAR":
        return <AlertTriangle style={{ ...iconStyle, color: "#ff9800" }} />;
      default:
        return <Info style={{ ...iconStyle, color: "#757575" }} />;
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "";

    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) return "";

      return `${date.getDate().toString().padStart(2, "0")}/${(
        date.getMonth() + 1
      )
        .toString()
        .padStart(2, "0")}/${date.getFullYear()} ${date
        .getHours()
        .toString()
        .padStart(2, "0")}:${date.getMinutes().toString().padStart(2, "0")}`;
    } catch (error) {
      return "";
    }
  };

  const getDoctorName = (medicoId) => {
    if (!auth.usuariosAssociados || !medicoId || medicoId === "ungrouped")
      return "Outros";

    const doctor = auth.usuariosAssociados.find((m) => m.id === medicoId);
    return doctor ? doctor.nome : "Médico";
  };

  const handleExecutarNotificacao = (notificacao) => {
    // Executa a ação sem remover a notificação
    onClickNotificacao(
      notificacao.id,
      notificacao.tipo,
      notificacao.cliente?.id || notificacao.cliente?._id,
      notificacao.dados,
      true // isPermanent: não deve ser removida
    );
    onClose(); // Fecha a modal após executar
  };

  const limparFiltros = () => {
    setDataInicio("");
    setDataFim("");
    setTipoFiltro("todos");
  };

  const tiposNotificacao = [
    { value: "todos", label: "Todos os tipos" },
    { value: "espera avaliação", label: "Espera Avaliação" },
    { value: "espera observação", label: "Espera Observação" },
    { value: "espera consulta", label: "Espera Consulta" },
    { value: "criou tratamento isolado", label: "Tratamento Isolado" },
    { value: "efetuou avaliação", label: "Efetuou Avaliação" },
    { value: "reagendamento", label: "Reagendamento" },
    { value: "limpezaRealizada", label: "Limpeza Realizada" },
    { value: "agendamentoProvisorioCriado", label: "Agendamento Provisório" },
    { value: "lembreteAgendamentoProvisorio", label: "Lembrete Agendamento" },
    { value: "EMPRESA_PARCEIRA_EXPIRADA", label: "Empresa Parceira Expirada" },
    {
      value: "EMPRESA_PARCEIRA_A_EXPIRAR",
      label: "Empresa Parceira a Expirar",
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      className="modalNotificacoesPermanentes"
    >
      <DialogTitle>
        <div className="modalNotificacoesPermanentes__header">
          <span>Histórico de Notificações</span>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent>
        {/* Filtros */}
        <div className="modalNotificacoesPermanentes__filtros">
          <TextField
            label="Data Início"
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="Data Fim"
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            select
            label="Tipo"
            value={tipoFiltro}
            onChange={(e) => setTipoFiltro(e.target.value)}
            size="small"
            style={{ minWidth: "200px" }}
          >
            {tiposNotificacao.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Button onClick={limparFiltros} variant="outlined" size="small">
            Limpar Filtros
          </Button>

          {!dataInicio && !dataFim && (
            <div className="modalNotificacoesPermanentes__filtros__info">
              📅 Mostrando notificações de hoje
            </div>
          )}
        </div>

        {/* Lista de notificações */}
        <div className="modalNotificacoesPermanentes__lista">
          {loading ? (
            <div className="modalNotificacoesPermanentes__loading">
              Carregando...
            </div>
          ) : filteredNotificacoes.length === 0 ? (
            <div className="modalNotificacoesPermanentes__empty">
              Nenhuma notificação encontrada
            </div>
          ) : (
            filteredNotificacoes.map((n) => (
              <div
                key={n.id}
                className="modalNotificacoesPermanentes__item"
                onClick={() => handleExecutarNotificacao(n)}
              >
                <div className="modalNotificacoesPermanentes__item__icon">
                  {getNotificationIcon(n.tipo)}
                </div>
                <div className="modalNotificacoesPermanentes__item__content">
                  {needsDoctorInfo && n.medicoId && (
                    <div className="modalNotificacoesPermanentes__item__doctor">
                      {getDoctorName(n.medicoId)}
                    </div>
                  )}
                  <div className="modalNotificacoesPermanentes__item__message">
                    {n.mensagem.split("\n").map((text, i) => (
                      <span key={i} style={{ display: "block" }}>
                        {text}
                      </span>
                    ))}
                  </div>
                  {n.dados?.observacao && (
                    <div
                      style={{
                        marginTop: "6px",
                        fontSize: "13px",
                        color: "#92400e",
                        background: "#fef3c7",
                        borderLeft: "3px solid #f59e0b",
                        borderRadius: "4px",
                        padding: "5px 9px",
                        wordBreak: "break-word",
                      }}
                    >
                      📝 {n.dados.observacao}
                    </div>
                  )}
                  <div className="modalNotificacoesPermanentes__item__datetime">
                    {formatDateTime(n.created_at)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ModalNotificacoesPermanentes;
